import React from 'react';
import { LinkedinIcon, BriefcaseIcon, GraduationCapIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { AspectRatio } from '../ui/aspect-ratio';

type TeamMember = {
  name: string;
  role: string;
  bio: {
    en: string;
    es: string;
  };
  imageSrc?: string;
  education?: {
    en: string;
    es: string;
  };
  experience?: {
    en: string;
    es: string;
  };
  linkedin?: string;
};

const teamMembers: TeamMember[] = [
  {
    name: "Hussein Fawzi",
    role: "Managing Partner",
    bio: {
      en: "With over 15 years of experience in building and scaling B2B SaaS businesses across Latin America and Europe.",
      es: "Con más de 15 años de experiencia en la construcción y escalamiento de negocios B2B SaaS en América Latina y Europa."
    },
    imageSrc: "/lovable-uploads/325dbc11-b85f-4bbd-ac2b-23905415c5de.png",
    experience: {
      en: "Chairman Directo Group",
      es: "Presidente Directo Group"
    },
    education: {
      en: "Y Combinator alumni",
      es: "Exalumno de Y Combinator"
    },
    linkedin: "https://www.linkedin.com/in/hussein-a-fawzi-4427031a4/"
  },
  {
    name: "Ronni Samir",
    role: "Chief Investment Officer",
    bio: {
      en: "With over a decade of experience in M&A and buy-and-build strategies for scaling B2B SaaS businesses across Latin America.",
      es: "Con más de una década de experiencia en M&A y estrategias de compra y construcción para escalar negocios B2B SaaS en América Latina."
    },
    imageSrc: "/lovable-uploads/062e011a-6054-4e22-9bb8-3ef795f23d6e.png",
    education: {
      en: "MS Finance, Jonkoping University",
      es: "Maestría en Finanzas, Universidad de Jonkoping"
    },
    experience: {
      en: "10+ years entrepreneur",
      es: "Más de 10 años como emprendedor"
    },
    linkedin: "https://www.linkedin.com/in/ronnisamir/"
  },
  {
    name: "Ignacio Lizarraga",
    role: "Chief Financial Officer",
    bio: {
      en: "With over a decade of experience in M&A, private equity, search funds, and banking across the US and Mexico.",
      es: "Con más de una década de experiencia en M&A, private equity, search funds y banca en Estados Unidos y México."
    },
    imageSrc: "/lovable-uploads/d67412b2-4dac-4f70-a82a-3c39286ebeaf.png",
    education: {
      en: "MBA, Chicago Booth",
      es: "MBA, Chicago Booth"
    },
    experience: {
      en: "Former Investment Professional at BlackRock",
      es: "Ex-Profesional de Inversiones en BlackRock"
    },
    linkedin: "https://www.linkedin.com/in/ignaciolizarraga/"
  }
];

const TeamSection: React.FC = () => {
  const { language, t } = useLanguage();
  
  return (
    <section className="py-20 md:py-28 bg-noa-antiflash-white dark:bg-noa-rich-black/90">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-12">
          <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white">
            {language === 'en' ? 'Our' : 'Nuestro'}
          </h2>
          <h2 className="font-space-grotesk text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-noa-rich-black dark:text-noa-antiflash-white">
            {language === 'en' ? 'team' : 'equipo'}<span className="text-blue-600 dark:text-blue-400">.</span>
          </h2>
        </div>
        <p className="text-xl sm:text-2xl font-light text-noa-charcoal dark:text-noa-antiflash-white/90 mb-16 max-w-3xl border-l-2 border-noa-rich-black dark:border-noa-antiflash-white pl-6">
          {t('team_description')}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {teamMembers.map((member, index) => (
            <Card key={index} className="bg-white dark:bg-noa-charcoal border-none shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col items-center">
                  <div className="w-full h-80 sm:h-96 md:h-[420px] relative overflow-hidden bg-gray-100 dark:bg-gray-800">
                    {member.imageSrc ? (
                      <div className="w-full h-full">
                        <img 
                          src={member.imageSrc} 
                          alt={member.name} 
                          className="w-full h-full object-cover"
                          style={{ 
                            objectPosition: member.name === "Hussein Fawzi" 
                              ? "center 15%" 
                              : member.name === "Ignacio Lizarraga" 
                                ? "center 60%" 
                                : "center"
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-blue-100 dark:bg-blue-900/30">
                        <span className="text-4xl font-bold text-blue-500">{member.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 w-full">
                    <h3 className="font-space-grotesk text-2xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {member.name}
                    </h3>
                    <p className="text-noa-paynes-gray dark:text-noa-antiflash-white/70 text-sm font-medium mt-1 mb-4">
                      {member.role}
                    </p>
                    
                    <p className="text-noa-charcoal dark:text-noa-antiflash-white/90 text-sm mb-6">
                      {member.bio[language]}
                    </p>
                    
                    <div className="border-t border-noa-charcoal/10 dark:border-noa-antiflash-white/10 pt-4 mt-4">
                      {member.education && (
                        <div className="flex items-center text-sm text-noa-charcoal dark:text-noa-antiflash-white/80 mb-2">
                          <GraduationCapIcon className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                          <span>{member.education[language]}</span>
                        </div>
                      )}
                      
                      {member.experience && (
                        <div className="flex items-center text-sm text-noa-charcoal dark:text-noa-antiflash-white/80 mb-2">
                          <BriefcaseIcon className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500 dark:text-blue-400" />
                          <span>{member.experience[language]}</span>
                        </div>
                      )}
                      
                      {member.linkedin && (
                        <a 
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-sm text-noa-charcoal hover:text-blue-600 dark:text-noa-antiflash-white/80 dark:hover:text-blue-400 transition-colors mt-3"
                        >
                          <LinkedinIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{language === 'en' ? 'LinkedIn Profile' : 'Perfil de LinkedIn'}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
